import { useEffect, useMemo, useState } from "react";
import { apiGet, prettifyApiError } from "@/shared/api";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { IconSearch } from "@/features/ordini/components/icons";

// Tab «Magazzino» (Davide 2026-07-06): giacenze BS COM dal mirror MATECPRO
// (catalog.warehouse_skus, lane warehouse_stock ogni 15'). quantity null =
// riga della vecchia fonte GSheet (giacenza sconosciuta).

interface WarehouseStockItem {
  sku: string;
  name: string;
  quantity: number | null;
  synced_at: string | null;
}

export function WarehouseStockSection() {
  const [items, setItems] = useState<WarehouseStockItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<{ items: WarehouseStockItem[]; total: number }>("/api/warehouse/stock")
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(prettifyApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (item) => item.sku.toLowerCase().includes(query) || item.name.toLowerCase().includes(query),
    );
  }, [items, search]);

  const inStock = useMemo(
    () => items.filter((item) => item.quantity === null || item.quantity > 0).length,
    [items],
  );
  const firstSyncedAt = items[0]?.synced_at;
  const lastSync = firstSyncedAt ? new Date(firstSyncedAt).toLocaleString("it-IT") : null;

  return (
    <Card title="Giacenze magazzino" className="mb-4">
      <p className="text-[12px] text-ink-2 -mt-1 mb-3">
        Mirror dell'app MATECPRO Logistics (prodotti con azienda BS COM), aggiornato ogni 15 minuti.
      </p>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <label className="flex items-center gap-2 flex-1 max-w-[320px] px-3 py-1.5 bg-paper border border-line rounded-full text-[12.5px] focus-within:border-ink-4 focus-within:bg-card">
          <IconSearch className="w-[13px] h-[13px] text-ink-2 fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round] shrink-0" />
          <input
            className="border-none bg-transparent flex-1 min-w-0 outline-none placeholder:text-ink-3"
            placeholder="Cerca SKU o nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Cerca nel magazzino"
          />
        </label>
        <span className="text-[11.5px] text-ink-2 tabular-nums">
          {items.length} referenze · <strong className="text-teal">{inStock} a stock</strong>
          {lastSync && ` · sync ${lastSync}`}
        </span>
      </div>

      <ErrorBanner message={error} />
      {loading && !error && <div className="text-[12.5px] text-ink-2">Carico giacenze…</div>}
      {!loading && !error && items.length === 0 && (
        <div className="text-[12.5px] text-ink-2">
          Nessun prodotto BS COM ancora censito su MATECPRO: marca i prodotti nostri con azienda «BS
          COM» nell'app e al prossimo sync compariranno qui.
        </div>
      )}
      {/* Ricerca che filtra a zero: senza questo ramo sotto la barra restava il vuoto
          mentre il contatore continuava a dire «N referenze» — non si distingueva
          «non è a magazzino» da «il pannello si è rotto». */}
      {!loading && !error && items.length > 0 && filtered.length === 0 && (
        <div className="text-[12.5px] text-ink-2">
          Nessuna referenza a magazzino per «{search.trim()}».
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto border border-line rounded-lg">
          <table className="w-full text-[12.5px]" data-testid="warehouse-stock-table">
            <thead className="sticky top-0 bg-paper-2 text-left">
              <tr className="text-[12px] text-ink-2 font-semibold">
                <th className="px-3 py-2 font-medium">SKU</th>
                <th className="px-3 py-2 font-medium">Prodotto</th>
                <th className="px-3 py-2 font-medium text-right">Giacenza</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.sku} className="border-t border-line">
                  <td className="px-3 py-1.5 tabular-nums text-ink-2">{item.sku}</td>
                  <td className="px-3 py-1.5 text-ink">{item.name || "—"}</td>
                  <td
                    className={[
                      "px-3 py-1.5 text-right tabular-nums tabular-nums",
                      item.quantity === null
                        ? "text-ink-2"
                        : item.quantity > 0
                          ? "text-teal font-semibold"
                          : "text-ink-2",
                    ].join(" ")}
                  >
                    {item.quantity === null ? "n/d" : item.quantity}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
